import { signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MasterViewModel } from '../../../base-classes/master-view-model';
import { ExecutionResultService } from '../../../services/execution-result.service';
import { ExecutionResult } from '../../../services/execution-results-api.service';
import { FuntionalAreaService } from '../../test-suite/services/test-suite.service';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { Package, Client } from '../../test-suite/models';
import { Scenario } from '../../scenario/models';
import moment from 'moment';
import { environment } from '../../../../environments/environment';
import { APP_CONFIG } from '../../../config/app.config';
import { ExecutionResultApiService } from '../services/execution-result-api.service';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/login/services/auth.service';
import { ClientService } from '../../client/services/client.service';

export class ExecutionResultViewModel extends MasterViewModel<ExecutionResult> {
  // Split view state
  functionalAreaTabs = signal<{ id: string; name: string; scenarioCount: number }[]>([]);
  selectedFaTabId = signal<string | null>(null);
  scenarioSummaries = signal<any[]>([]); // scenarios for current FA tab + page
  selectedScenarioId = signal<string | null>(null);
  selectedExecutionRunId = signal<string | null>(null);
  selectedScenarioData = signal<any>(null); // full data for selected scenario header
  scenarioSteps = signal<any[]>([]); // lazy-loaded steps for selected scenario
  totalScenarios = signal<number>(0);
  currentPage = signal<number>(0); // 0-indexed for p-paginator
  pageSize = signal<number>(10);
  isLoadingScenarios = signal<boolean>(false);
  isLoadingSteps = signal<boolean>(false);
  scenarioSearchText: string = '';
  expandedStepId = signal<string | null>(null);
  hasSearched = signal<boolean>(false); // tracks if user has clicked Search

  packages = signal<Package[]>([]);
  functionalAreaOptions = signal<{ id: string; name: string; displayLabel: string }[]>([]);
  scenarios = signal<Scenario[]>([]);
  selectedPackage = signal<Package | null>(null);
  selectedFunctionalAreaIds = signal<string[]>([]);
  selectedPackageId = signal<string | null>(null);
  selectedScenario = signal<string[]>([]);

  clients = signal<Client[]>([]);
  clientOptions = signal<{ label: string; value: string }[]>([]);
  selectedClientFilter = signal<string>('');
  isClientDropdownDisabled = signal<boolean>(false);

  packageOptions: any[] = [];
  testStatus: any[] = [];
  filterStatus: string = '';
  dateRange = signal<Date[] | null>(null);
  maxDate = new Date();
  type_test: any[] = [];
  selectedResult = signal<ExecutionResult | null>(null);

  treeDataCache: { [key: string]: any[] } = {};
  displayScreenshotDialog = signal(false);
  selectedScreenshot = signal<string>("");

  // Add cancellation subject to prevent race conditions
  private cancelPendingRequests$ = new Subject<void>();
  private pendingNotificationFilter: { scenarioIds: string[]; executionRunId?: string | null; clientId?: string | null; packageId?: string | null; functionalAreaId?: string | null; testTypeId?: string | null; tenantId?: string | null } | null = null;
  private pendingTenantId: string | null = null;
  private filterExecutionRunId: string | null = null;

  selectedTestTypeIds = signal<string[]>([]);
  selectedTestTypeFilter = signal<string>('');

  // Tenant filter
  clientTenants = signal<any[]>([]);
  tenantOptions = signal<{ label: string; value: string }[]>([]);
  selectedTenantId = signal<string>('');

  constructor(
    http: HttpClient,
    private executionResultService: ExecutionResultService,
    private funtionalAreaService: FuntionalAreaService,
    private scenarioService: ScenarioService,
    private authService: AuthService,
    private clientService?: ClientService
  ) {
    const baseUrl = `${environment.apiUrl}/execution-results`;
    const moduleName = 'Execution Results';
    super(http, baseUrl, moduleName);
  }

  init(): void {
    this.loadClients();
    this.loadReferenceData();
    this.loadStatusOptions();
    this.loadTenants();
    // Initialize date range to last 7 days
    const today = new Date();
    const last7Days = new Date();
    last7Days.setDate(today.getDate() - 7);
    this.dateRange.set([last7Days, today]);
  }

  loadReferenceData(): void {
    this.scenarioService.getReferenceList(['TEST_TYPE']).subscribe((dataResult: any) => {
      this.type_test = dataResult;
    });
  }

  loadClients(): void {
    this.scenarioService.getClientsAll().subscribe({
      next: (response: any) => {
        const clients = response?.data;
        const clientMappedOptions = clients.map((c: any) => ({ label: c.name, value: c.id }));

        const userInfo = this.authService.getUserInfoFromToken();
        const isSystemUser = userInfo?.isSystemUser === true;
        const opts = isSystemUser
          ? [{ label: 'Master Client', value: 'default' }, ...clientMappedOptions]
          : clientMappedOptions;

        this.clients.set(clients);
        this.clientOptions.set(opts);

        if (!isSystemUser && clientMappedOptions.length === 1) {
          const singleClientId = clientMappedOptions[0].value;
          this.selectedClientFilter.set(singleClientId);
          this.isClientDropdownDisabled.set(true);
          // Check pending notification filter before calling onClientFilterChange
          if (this.pendingNotificationFilter) {
            const filterData = this.pendingNotificationFilter;
            this.pendingNotificationFilter = null;
            this.applyFilterData(filterData);
          } else {
            this.onClientFilterChange(singleClientId);
          }
        } else {
          this.isClientDropdownDisabled.set(false);
          // Apply pending notification filter for multi-client/system users
          if (this.pendingNotificationFilter) {
            const filterData = this.pendingNotificationFilter;
            this.pendingNotificationFilter = null;
            this.applyFilterData(filterData);
          }
        }
      },
      error: () => {
        const userInfo = this.authService.getUserInfoFromToken();
        const isSystemUser = userInfo?.isSystemUser === true;
        const fallbackOpts = isSystemUser
          ? [{ label: 'Master Client', value: 'default' }]
          : [];
        this.clientOptions.set(fallbackOpts);
        this.isClientDropdownDisabled.set(false);
      }
    });
  }

  public testTypeFilterOptions() {
    return this.type_test.map(item => ({
      label: item.referenceValue,
      value: item.id
    }));
  }

  public filterByTestType(testTypeIds: string[]): void {
    this.selectedTestTypeIds.set(testTypeIds || []);
    
    // Clear functional area when test type changes
    this.selectedFunctionalAreaIds.set([]);
    this.selectedScenario.set([]);
    this.scenarios.set([]);
    
    // Update functional area dropdown based on selected test types
    const allFunctionalAreas = this.packages();
    
    if (!testTypeIds || testTypeIds.length === 0) {
      // No test type selected - show all functional areas
      this.functionalAreaOptions.set(allFunctionalAreas as any);
    } else {
      // Filter functional areas by selected test types
      const filteredAreas = allFunctionalAreas.filter((area: any) => 
        area.testTypeId && testTypeIds.includes(area.testTypeId)
      );
      this.functionalAreaOptions.set(filteredAreas as any);
    }
  }

  public clearTestTypeFilter(): void {
    this.selectedTestTypeIds.set([]);
    this.selectedFunctionalAreaIds.set([]);
    this.selectedScenario.set([]);
    this.scenarios.set([]);
    
    // Restore all functional areas when clearing test type
    this.functionalAreaOptions.set(this.packages() as any);
  }

  onClientFilterChange(clientId: string): void {
    this.selectedClientFilter.set(clientId);
    this.selectedPackageId.set(null);
    this.selectedFunctionalAreaIds.set([]);
    this.selectedTestTypeIds.set([]);
    this.selectedScenario.set([]);
    this.scenarios.set([]);
    this.filterStatus = '';
    this.selectedTenantId.set('');
    // Clear split-view state
    this.scenarioSteps.set([]);
    this.scenarioSummaries.set([]);
    this.selectedScenarioId.set(null);
    this.selectedExecutionRunId.set(null);
    this.hasSearched.set(false);

    if (clientId) {
      this.initDateRange();

      if (clientId === 'default') {
        this.loadFunctionalAreas();
        this.executionResultService.getAllPackages().subscribe({
          next: (packages) => {
            this.packageOptions = packages
              .map((item: any) => ({
                id: item.id,
                packageName: item.packageName
              }));

            this.selectedPackageId.set(null);
          },
          error: (error) => {
            this.packageOptions = [];
          }
        });
      }
      else {
        this.functionalAreaList(clientId);
        this.packageList(clientId);
        this.loadClientTenants(clientId);
      }
    } else {
      this.dateRange.set(null);
      this.packageOptions = [];
      this.packages.set([]);
      this.selectedPackageId.set(null);
      this.tenantOptions.set([]);
    }

    // Apply pending notification filter (set before init's async callbacks ran)
    if (this.pendingNotificationFilter) {
      const filterData = this.pendingNotificationFilter;
      this.pendingNotificationFilter = null;
      this.applyFilterData(filterData);
    }
  }

  packageList(clientId: string): void {
    this.executionResultService.getAllPackagesById(clientId).subscribe({
      next: (packages) => {
        const mapped = packages
          .filter((item: any) => item.package)
          .map((item: any) => ({
            id: item.package.id,
            packageName: item.package.packageName
          }));

        const uniqueMap = new Map<string, { id: string; packageName: string }>();
        mapped.forEach((pkg: any) => {
          const key = `${pkg.id}-${pkg.packageName}`;
          if (!uniqueMap.has(key)) {
            uniqueMap.set(key, pkg);
          }
        });
        this.packageOptions = Array.from(uniqueMap.values());
      },
      error: (error) => {
        this.packageOptions = [];
      }
    });
  }

  functionalAreaList(clientId: string): void {
    this.funtionalAreaService.getAllFunctionalAreas().subscribe({
      next: (res: any) => {
        const functionalAreas = res
          .filter((item: any) => {
            return item.clientPackageMapping?.client?.id === clientId;
          })
          .map((item: any) => ({
            id: item.id,
            name: item.name,
            code: item.testType?.referenceValue || '',
            testTypeId: item.testType?.id || null,
            displayLabel: item.testType?.referenceValue 
              ? `${item.name} (${item.testType.referenceValue})`
              : item.name
          }));

        this.packages.set(functionalAreas);
        this.functionalAreaOptions.set(functionalAreas);
      },
      error: (error: any) => {
        this.packages.set([]);
        this.functionalAreaOptions.set([]);
      }
    });
  }

  private loadTenants(): void {
    if (!this.clientService) return;
    this.clientService.getTenantsAll().subscribe({
      next: (tenants: any[]) => {
        this.clientTenants.set(tenants || []);
        this.tenantOptions.set(
          (tenants || []).map((t: any) => ({
            label: t.tenantName || t.tenentType || t.tenantType || t.description || 'Unknown',
            value: t.id
          }))
        );
        // Apply deferred tenant selection from notification redirect
        if (this.pendingTenantId) {
          this.selectedTenantId.set(this.pendingTenantId);
          this.pendingTenantId = null;
        }
      },
      error: () => {
        this.clientTenants.set([]);
        this.tenantOptions.set([]);
      }
    });
  }

  private loadClientTenants(clientId: string): void {
    if (!this.clientService) return;
    this.clientService.getClientTenants(clientId).subscribe({
      next: (tenants: any[]) => {
        this.clientTenants.set(tenants || []);
        this.tenantOptions.set(
          (tenants || []).map((t: any) => ({
            label: t.tenantName || t.tenant?.tenentType || t.description || 'Unknown',
            value: t.id
          }))
        );
        if (this.pendingTenantId) {
          this.selectedTenantId.set(this.pendingTenantId);
          this.pendingTenantId = null;
        }
      },
      error: () => {
        this.tenantOptions.set([]);
      }
    });
  }

  onTenantFilterChange(tenantId: string | null): void {
    this.selectedTenantId.set(tenantId || '');
  }

  clearTenantFilter(): void {
    this.selectedTenantId.set('');
  }

  clearClientFilter(): void {
    this.selectedClientFilter.set('');
    this.dateRange.set(null);
    this.packageOptions = [];
    this.selectedPackageId.set(null);
    this.selectedFunctionalAreaIds.set([]);
    this.selectedTestTypeIds.set([]);
    this.selectedScenario.set([]);
    this.scenarios.set([]);
    this.filterStatus = '';
    this.selectedTenantId.set('');
    this.scenarioSteps.set([]);
    this.scenarioSummaries.set([]);
    this.functionalAreaTabs.set([]);
    this.totalScenarios.set(0);
    this.selectedFaTabId.set(null);
    this.selectedScenarioId.set(null);
    this.selectedExecutionRunId.set(null);
    this.hasSearched.set(false);
  }

  loadFunctionalAreas(): void {
    this.funtionalAreaService.getAllFunctionalAreas().subscribe({
      next: (res: any) => {
        const functionalAreas = res
          .filter((item: any) => item.isActive && item.clientPackageMappingId === null)
          .map((item: any) => ({
            id: item.id,
            name: item.name,
            code: item.testType?.referenceValue || '',
            testTypeId: item.testType?.id || null,
            displayLabel: item.testType?.referenceValue 
              ? `${item.name} (${item.testType.referenceValue})`
              : item.name
          }));

        this.packages.set(functionalAreas);
        this.functionalAreaOptions.set(functionalAreas);
      },
      error: (error: any) => {
        this.packages.set([]);
        this.functionalAreaOptions.set([]);
      }
    });
  }

  loadScenarios(functionalAreaId: string): void {
    this.scenarioService.getScenariosByfunctionalAreaId(functionalAreaId).subscribe({
      next: (scenarios) => {
        this.scenarios.set(scenarios || []);
      },
      error: (error) => {
        this.scenarios.set([]);
      }
    });
  }

  loadScenariosForMultipleFunctionalAreas(functionalAreaIds: string[]): void {
    if (!functionalAreaIds || functionalAreaIds.length === 0) {
      this.scenarios.set([]);
      return;
    }

    const scenarioRequests = functionalAreaIds.map(id =>
      this.scenarioService.getScenariosByfunctionalAreaId(id)
    );

    forkJoin(scenarioRequests).subscribe({
      next: (allScenarios) => {
        const uniqueScenarios = allScenarios
          .flat()
          .filter((scenario, index, self) =>
            index === self.findIndex(s => s.id === scenario.id)
          );
        this.scenarios.set(uniqueScenarios);
      },
      error: (error) => {
        this.scenarios.set([]);
      }
    });
  }

  onPackageChange(functionalAreaIds: string[] | null): void {
    // Only allow selection if test type is selected
    if (!this.selectedTestTypeIds() || this.selectedTestTypeIds().length === 0) {
      this.selectedFunctionalAreaIds.set([]);
      this.selectedScenario.set([]);
      this.scenarios.set([]);
      return;
    }
    
    this.selectedFunctionalAreaIds.set(functionalAreaIds || []);
    this.selectedScenario.set([]);
    this.scenarios.set([]);

    if (functionalAreaIds && functionalAreaIds.length > 0) {
      this.loadScenariosForMultipleFunctionalAreas(functionalAreaIds);
    }
  }

  onPackageFilterChange(packageId: string | null): void {
    this.selectedPackageId.set(packageId);
  }

  onScenarioChange(selectedScenarioIds: string[] | null): void {
    this.selectedScenario.set(selectedScenarioIds || []);
  }

  /**
   * Called when user clicks Search button.
   * Calls POST /execution-results/scenario-summary with current filters.
   * Populates FA tabs and first page of scenarios.
   */
  onSearch(): void {
    this.isLoadingScenarios.set(true);
    this.hasSearched.set(true);
    this.selectedScenarioId.set(null);
    this.selectedExecutionRunId.set(null);
    this.scenarioSteps.set([]);

    const filterPayload = this.buildFilterPayload();

    this.executionResultService.getScenarioSummary(filterPayload, 1, this.pageSize())
      .pipe(takeUntil(this.cancelPendingRequests$))
      .subscribe({
        next: (result: any) => {
          this.functionalAreaTabs.set(result.functionalAreas || []);
          this.scenarioSummaries.set(result.data || []);
          this.totalScenarios.set(result.total || 0);
          this.currentPage.set(0);
          // Auto-select first FA tab and load its scenarios
          if (result.functionalAreas?.length > 0) {
            this.selectedFaTabId.set(result.functionalAreas[0].id);
            if (this.selectedScenario().length === 0) {
              this.loadScenariosForFa(result.functionalAreas[0].id, 1);
            }
          }
          this.isLoadingScenarios.set(false);
        },
        error: () => {
          this.scenarioSummaries.set([]);
          this.totalScenarios.set(0);
          this.isLoadingScenarios.set(false);
        }
      });
  }

  /**
   * Load scenarios for a specific FA tab with pagination.
   */
  private loadScenariosForFa(faId: string, page: number): void {
    this.isLoadingScenarios.set(true);

    const filterPayload = this.buildFilterPayload();
    // Add FA filter (API expects array for functionalAreaId)
    filterPayload.filter.push({ name: 'functionalAreaId', value: [faId] });

    this.executionResultService.getScenarioSummary(filterPayload, page, this.pageSize())
      .pipe(takeUntil(this.cancelPendingRequests$))
      .subscribe({
        next: (result: any) => {
          this.scenarioSummaries.set(result.data || []);
          this.totalScenarios.set(result.total || 0);
          this.isLoadingScenarios.set(false);
        },
        error: () => {
          this.scenarioSummaries.set([]);
          this.isLoadingScenarios.set(false);
        }
      });
  }

  /**
   * Build filter payload from current filter state.
   */
  private buildFilterPayload(): any {
    const clientIdValue = this.selectedClientFilter() === 'default' ? null : this.selectedClientFilter() || "";

    // When navigating from notification, filter by executionRunId (exact run only)
    if (this.filterExecutionRunId) {
      return {
        filter: [
          { name: "executionRunId", value: this.filterExecutionRunId },
        ]
      };
    }

    return {
      filter: [
        { name: "clientId", value: clientIdValue },
        { name: "testTypeId", value: this.selectedTestTypeIds() },
        { name: "functionalAreaId", value: this.selectedFunctionalAreaIds() },
        { name: "scenarioId", value: this.selectedScenario() },
        { name: "packageId", value: this.selectedPackageId() || "" },
        { name: "statusId", value: this.filterStatus || "" },
        { name: "tenantId", value: this.selectedTenantId() || "" },
        { name: "executionRunId", value: "" },
        { name: "fromDate", value: this.dateRange()?.[0] ? moment(this.dateRange()![0]).format('YYYY-MM-DD') : "" },
        { name: "toDate", value: this.dateRange()?.[1] ? moment(this.dateRange()![1]).format('YYYY-MM-DD') : "" }
      ]
    };
  }

  onFaTabChange(faId: string): void {
    this.selectedFaTabId.set(faId);
    this.currentPage.set(0);
    this.selectedScenarioId.set(null);
    this.selectedExecutionRunId.set(null);
    this.scenarioSteps.set([]);
    this.loadScenariosForFa(faId, 1);
  }

  onScenarioPageChange(event: any): void {
    this.currentPage.set(event.page);
    const faId = this.selectedFaTabId();
    if (faId) {
      this.loadScenariosForFa(faId, event.page + 1); // p-paginator is 0-indexed
    }
  }

  /**
   * Select a scenario and lazy-load its steps.
   * If executionRunId is provided (from scenario summary), fetches steps for that specific run.
   * Otherwise falls back to scenario-based fetch with time-window grouping.
   */
  onScenarioSelect(scenarioId: string, executionRunId?: string): void {
    // Dedup: skip if same scenario AND same run already selected
    const sameScenario = this.selectedScenarioId() === scenarioId;
    const sameRun = this.selectedExecutionRunId() === (executionRunId || null);
    if (sameScenario && sameRun) return; // already selected

    this.selectedScenarioId.set(scenarioId);
    this.selectedExecutionRunId.set(executionRunId || null);
    this.expandedStepId.set(null);
    this.isLoadingSteps.set(true);

    // Find scenario summary data for the header (match by executionRunId if available)
    const summary = executionRunId
      ? this.scenarioSummaries().find((s: any) => s.executionRunId === executionRunId)
      : this.scenarioSummaries().find((s: any) => s.scenarioId === scenarioId);
    this.selectedScenarioData.set(summary);

    // If executionRunId is available, fetch steps for that specific execution run
    const stepsObservable = executionRunId
      ? this.executionResultService.loadExecutionResultsByRunId(executionRunId)
      : this.executionResultService.loadExecutionResultsByScenario(scenarioId);

    stepsObservable.subscribe({
      next: (steps: any[]) => {
        // Sort by startedAt descending to find the latest execution run
        const allSorted = [...steps].sort((a: any, b: any) => {
          const tA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          const tB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          return tB - tA; // DESC
        });

        // Group by execution run: steps within 30 minutes of the latest step belong to the same run
        // This isolates the most recent execution from historical ones
        let latestRunSteps = allSorted;
        if (allSorted.length > 0) {
          const latestTime = allSorted[0].startedAt ? new Date(allSorted[0].startedAt).getTime() : 0;
          const RUN_WINDOW_MS = 30 * 60 * 1000; // 30 minutes
          latestRunSteps = allSorted.filter((s: any) => {
            const t = s.startedAt ? new Date(s.startedAt).getTime() : 0;
            return latestTime - t < RUN_WINDOW_MS;
          });
        }

        // Re-sort the latest run steps ascending (step execution order)
        const sorted = latestRunSteps.sort((a: any, b: any) => {
          const tA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          const tB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          return tA - tB;
        });

        // Enhance steps with computed fields for template binding
        const enhanced = sorted.map((step: any) => {
          let action = '', duration: number | undefined, error = '';
          try {
            if (step.logText) {
              const log = JSON.parse(step.logText);
              action = log.action || '';
              duration = log.duration;
              error = log.error || '';
            }
          } catch {}
          // Resolve status from multiple possible sources
          const statusRef = step.status?.referenceValue || step.status?.value || step.status?.name || '';
          const statusLower = statusRef.toLowerCase();
          const stepStatus = statusLower.includes('pass') || statusLower.includes('success') ? 'success'
            : statusLower.includes('fail') || statusLower.includes('error') ? 'failed'
            : statusLower.includes('stop') || statusLower.includes('skip') ? 'stopped'
            : 'unknown';
          return {
            ...step,
            parsedAction: action,
            parsedDuration: duration ? (duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(2)}s`) : '-',
            parsedError: error,
            stepStatus,
            screenshotFullUrl: step.screenshotUrl ? `${APP_CONFIG.FILE_URL}/${step.screenshotUrl}` : ''
          };
        });
        this.scenarioSteps.set(enhanced);
        this.isLoadingSteps.set(false);
      },
      error: () => {
        this.scenarioSteps.set([]);
        this.isLoadingSteps.set(false);
      }
    });
  }

  onStepExpand(stepId: string): void {
    this.expandedStepId.set(this.expandedStepId() === stepId ? null : stepId);
  }

  onScenarioSearchChange(text: string): void {
    this.scenarioSearchText = text;
    this.scenarioSearchFilter.set(text.toLowerCase().trim());
  }

  // Signal for search filter text (drives the filtered list)
  scenarioSearchFilter = signal<string>('');

  /**
   * Returns scenarioSummaries filtered by search text (client-side).
   */
  filteredScenarioSummaries(): any[] {
    const filter = this.scenarioSearchFilter();
    const summaries = this.scenarioSummaries();
    if (!filter) return summaries;
    return summaries.filter((sc: any) => {
      const name = (sc.scenarioName || sc.scenarioDisplayId || '').toLowerCase();
      const executedBy = (sc.executedByName || '').toLowerCase();
      const tenant = (sc.tenantName || '').toLowerCase();
      return name.includes(filter) || executedBy.includes(filter) || tenant.includes(filter);
    });
  }

  clearResults(): void {
    this.executionResultService.clearAllResults();
    this.scenarioSteps.set([]);
    this.scenarioSummaries.set([]);
    this.selectedScenarioId.set(null);
    this.selectedExecutionRunId.set(null);
  }

  exportResults(): void {
    const dataStr = JSON.stringify(this.scenarioSteps(), null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `execution-results-${new Date().toISOString()}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  viewScreenshot(screenshotUrl: string): void {
    this.selectedScreenshot.set(`${APP_CONFIG.FILE_URL}/${screenshotUrl}`);
    this.displayScreenshotDialog.set(true);
  }

  // Status Helpers
  getStatusSeverity(status: string): 'success' | 'danger' | 'warn' | 'info' {
    const lowerStatus = status.toLowerCase();
    switch (lowerStatus) {
      case 'success':
        return 'success';
      case 'failed':
        return 'danger';
      case 'stopped':
        return 'warn';
      default:
        return 'info';
    }
  }

  getReadableStatus(result: any): string {
    if (result.status?.referenceValue) {
      return result.status.referenceValue;
    }

    try {
      if (result.logText) {
        const logData = JSON.parse(result.logText);
        if (logData.status) {
          return logData.status;
        }
      }
    } catch (error) {
      // JSON parsing failed, continue to fallback
    }

    return result.status || 'pending';
  }

  formatDuration(ms: number | undefined): string {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  loadStatusOptions(): void {
    this.executionResultService.getReferenceList(['TEACC']).subscribe({
      next: (dataResult: any) => {
        this.testStatus = dataResult;
      },
      error: () => {
        this.testStatus = [];
      }
    });
  }

  public getTestStatusOptions() {
    return this.testStatus.map(item => ({
      value: item.referenceValue,
      id: item.id,
    }));
  }

  onStatusFilterChange(status: string | null): void {
    this.filterStatus = status || '';
  }

  clearStatusFilter(): void {
    this.filterStatus = '';
  }

  onStatusFilterClear(): void {
    this.filterStatus = '';
  }

  onApplyFilters(): void {
    // Trigger search with current filters
    this.onSearch();
  }

  onResetFilters(): void {
    const userInfo = this.authService.getUserInfoFromToken();
    const isSystemUser = userInfo?.isSystemUser === true;
    const clientCount = this.clientOptions().filter(opt => opt.value !== 'default').length;

    this.filterExecutionRunId = null;

    if (!isSystemUser && clientCount === 1) {
      this.selectedFunctionalAreaIds.set([]);
      this.selectedTestTypeIds.set([]);
      this.selectedScenario.set([]);
      this.selectedPackageId.set(null);
      this.selectedTenantId.set('');
      this.filterStatus = '';
      this.scenarios.set([]);
      this.scenarioSteps.set([]);
      this.scenarioSummaries.set([]);
      this.functionalAreaTabs.set([]);
      this.totalScenarios.set(0);
      this.selectedFaTabId.set(null);
      this.selectedScenarioId.set(null);
      this.selectedExecutionRunId.set(null);
      this.hasSearched.set(false);
    } else {
      this.selectedClientFilter.set('');
      this.selectedFunctionalAreaIds.set([]);
      this.selectedTestTypeIds.set([]);
      this.selectedScenario.set([]);
      this.selectedPackageId.set(null);
      this.selectedTenantId.set('');
      this.filterStatus = '';
      this.scenarios.set([]);
      this.packageOptions = [];
      this.dateRange.set(null);
      this.scenarioSteps.set([]);
      this.scenarioSummaries.set([]);
      this.functionalAreaTabs.set([]);
      this.totalScenarios.set(0);
      this.selectedFaTabId.set(null);
      this.selectedScenarioId.set(null);
      this.selectedExecutionRunId.set(null);
      this.hasSearched.set(false);
    }

    this.cancelPendingRequests$.next();
  }

  initDateRange(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sixDaysAgo = new Date();
    sixDaysAgo.setDate(today.getDate() - 6);
    sixDaysAgo.setHours(0, 0, 0, 0);

    const newDateRange: Date[] = [
      new Date(sixDaysAgo.getFullYear(), sixDaysAgo.getMonth(), sixDaysAgo.getDate()),
      new Date(today.getFullYear(), today.getMonth(), today.getDate())
    ];

    this.dateRange.set(newDateRange);
  }

  clearFunctionalAreaFilter(): void {
    this.selectedFunctionalAreaIds.set([]);
    this.selectedScenario.set([]);
    this.scenarios.set([]);
  }

  clearScenarioFilter(): void {
    this.selectedScenario.set([]);
  }

  clearPackageFilter(): void {
    this.selectedPackageId.set(null);
  }

  /**
   * Filter functional areas based on selected test type(s)
   */
  private applyFunctionalAreaFiltering(): void {
    const selectedTestTypeIds = this.selectedTestTypeIds();
    const allFunctionalAreas = this.functionalAreaOptions();
    
    if (!selectedTestTypeIds || selectedTestTypeIds.length === 0) {
      // No test type selected - restore all functional areas
      this.functionalAreaOptions.set(this.packages() as any);
      return;
    }
    
    // Filter functional areas that match any of the selected test types
    const filteredAreas = allFunctionalAreas.filter((area: any) => 
      area.testTypeId && selectedTestTypeIds.includes(area.testTypeId)
    );
    
    this.functionalAreaOptions.set(filteredAreas);
  }

  /**
   * Restore all functional areas for the currently selected client
   */
  private restoreFunctionalAreasForClient(): void {
    const currentClientId = this.selectedClientFilter();
    
    if (!currentClientId) {
      this.functionalAreaOptions.set([]);
      this.packages.set([]);
      return;
    }
    
    if (currentClientId === 'default') {
      this.loadFunctionalAreas();
    } else {
      this.functionalAreaList(currentClientId);
    }
  }

  /**
   * Check if any filters are currently active
   */
  hasActiveFilters(): boolean {
    return !!(
      this.selectedClientFilter() ||
      this.selectedFunctionalAreaIds().length > 0 ||
      this.selectedScenario().length > 0 ||
      this.selectedPackageId() ||
      this.selectedTenantId() ||
      this.filterStatus ||
      this.dateRange()
    );
  }

  parseLogText(logText: string): any {
    if (!logText) {
      return null;
    }

    try {
      const parsed = JSON.parse(logText);
      return parsed;
    } catch (e) {
      return null;
    }
  }

  /**
    * Get scenario name - only scenario name, no fallbacks
    */
  getScenarioName(result: any): string {
    return result.scenario?.scenarioName || result.searchableScenario || '-';
  }

  /**
   * Clean up resources and cancel pending requests
   * Call this method when component is destroyed (ngOnDestroy)
   */
  destroy(): void {
    this.cancelPendingRequests$.next();
    this.cancelPendingRequests$.complete();
  }

  // ====== SPLIT VIEW COMPUTED SIGNALS ======

  /**
   * Get the name of the currently selected scenario
   */
  selectedScenarioName(): string {
    if (!this.selectedScenarioData()) {
      return '';
    }
    return this.selectedScenarioData().scenarioName || this.selectedScenarioData().scenarioId || '';
  }

  /**
   * Get the functional area name for the selected scenario
   */
  selectedFaName(): string {
    if (!this.selectedScenarioData()) {
      return '';
    }
    return this.selectedScenarioData().functionalAreaName || '';
  }

  /**
   * Get the formatted date for the selected scenario
   */
  selectedScenarioDate(): string {
    if (!this.selectedScenarioData()) {
      return '';
    }
    const dateStr = this.selectedScenarioData().latestStartedAt;
    if (!dateStr) {
      return '';
    }
    try {
      return moment(dateStr).format('MM/DD/YYYY HH:mm');
    } catch {
      return dateStr;
    }
  }

  /**
   * Get passed count for selected scenario
   */
  selectedScenarioPassed(): number {
    if (!this.selectedScenarioData()) {
      return 0;
    }
    return this.selectedScenarioData().passed || 0;
  }

  /**
   * Get failed count for selected scenario
   */
  selectedScenarioFailed(): number {
    if (!this.selectedScenarioData()) {
      return 0;
    }
    return this.selectedScenarioData().failed || 0;
  }

  /**
   * Get stopped count for selected scenario
   */
  selectedScenarioStopped(): number {
    if (!this.selectedScenarioData()) {
      return 0;
    }
    return this.selectedScenarioData().stopped || 0;
  }

  /**
   * Get the scenario steps from scenarioSteps signal
   */
  selectedScenarioSteps(): any[] {
    return this.scenarioSteps();
  }

  /**
   * Get step status (success, failed, stopped)
   */
  getStepStatus(step: any): string {
    if (!step) {
      return 'unknown';
    }
    // Map status codes to readable format
    const status = step.status || step.searchableStatus || '';
    if (status.toLowerCase().includes('pass') || status === '1') {
      return 'success';
    } else if (status.toLowerCase().includes('fail') || status === '2') {
      return 'failed';
    } else if (status.toLowerCase().includes('stop') || status === '3') {
      return 'stopped';
    }
    return 'unknown';
  }

  /**
   * Apply filters from notification (executionRunId and scenarioIds)
   * Triggered when user clicks "View More Results" from a notification
   */
  applyNotificationFilters(
    executionRunId: string,
    scenarioIdsRaw: string | null,
    context?: { clientId: string | null; packageId: string | null; functionalAreaId: string | null; testTypeId: string | null; tenantId?: string | null }
  ): void {
    const ids = scenarioIdsRaw ? scenarioIdsRaw.split(',').filter(Boolean) : [];
    const filterData = { scenarioIds: ids, executionRunId, ...context };

    // If clients are already loaded (user already on this page), apply immediately
    if (this.clientOptions().length > 0) {
      this.applyFilterData(filterData);
    } else {
      // Fresh page load: store as pending, onClientFilterChange will apply after loadClients
      this.pendingNotificationFilter = filterData;
    }
  }

  private applyFilterData(data: { scenarioIds: string[]; executionRunId?: string | null; clientId?: string | null; packageId?: string | null; functionalAreaId?: string | null; testTypeId?: string | null; tenantId?: string | null }): void {
    const clientId = data.clientId || '';

    if (clientId) {
      this.selectedClientFilter.set(clientId);
      this.initDateRange();

      // Load functional areas for this client to populate the dropdown options
      if (clientId === 'default') {
        this.loadFunctionalAreas();
      } else {
        this.functionalAreaList(clientId);
        this.packageList(clientId);
      }
    }

    if (data.testTypeId) {
      this.selectedTestTypeIds.set([data.testTypeId]);
    }
    if (data.functionalAreaId) {
      this.selectedFunctionalAreaIds.set([data.functionalAreaId]);
      // Load scenarios for the functional area to populate scenario dropdown
      this.loadScenariosForMultipleFunctionalAreas([data.functionalAreaId]);
    }
    if (data.packageId) {
      this.selectedPackageId.set(data.packageId);
    }
    if (data.scenarioIds.length > 0) {
      this.selectedScenario.set(data.scenarioIds);
    }
    if (data.tenantId) {
      // Load client-specific tenants (the stored tenantId is a client-tenant mapping ID)
      if (clientId && this.clientService) {
        this.clientService.getClientTenants(clientId).subscribe({
          next: (tenants: any[]) => {
            this.clientTenants.set(tenants || []);
            this.tenantOptions.set(
              (tenants || []).map((t: any) => ({
                label: t.tenant?.tenentType ? `${t.tenantName} (${t.tenant.tenentType})` : (t.tenantName || t.tenantType || t.description),
                value: t.id
              }))
            );
            this.selectedTenantId.set(data.tenantId!);
          },
          error: () => {}
        });
      } else {
        this.selectedTenantId.set(data.tenantId);
      }
    }
    if (data.executionRunId) {
      this.filterExecutionRunId = data.executionRunId;
    }
    this.hasSearched.set(true);
    this.onSearch();
  }

}